import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getDanhMucSanPhamKhacPhucLoi, getListThuongHieuKhacPhucLoi, getSanPhamKhacPhucLoi } from '../../../redux/category/action';

const ITEMS_PER_PAGE = 12;

const ProductPlaceholder = ({ brand }) => {
  const colors = {
    ECOVACS: '#2563eb',
    TINECO: '#0891b2',
    XIAOMI: '#f97316',
    'LG ELECTRONICS': '#dc2626',
    ROBOROCK: '#7c3aed',
    DYSON: '#d97706',
  };
  const color = colors[brand?.toUpperCase()] || '#64748b';

  return (
    <div
      style={{
        width: '100%',
        height: 200,
        background: `linear-gradient(135deg, ${color}18, ${color}30)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
      </svg>
    </div>
  );
};

const paginationBtn = (active) => ({
  width: 36,
  height: 36,
  border: active ? 'none' : '1.5px solid #e2e8f0',
  borderRadius: 8,
  background: active ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : '#fff',
  color: active ? '#fff' : '#555',
  fontWeight: active ? 700 : 400,
  fontSize: 14,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all .15s',
  opacity: active ? 1 : undefined,
  ':disabled': { opacity: 0.3, cursor: 'not-allowed' },
});

const DanhMucSanPham = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const brand = params.get('brand');
  const model = params.get('model');

  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchInput, setSearchInput] = useState(model || '');
  const [currentPage, setCurrentPage] = useState(1);

  const listThuongHieu = useSelector((store) => store.categories.listThuongHieu);
  const listDanhMuc = useSelector((store) => store.categories.listDanhMuc);
  const listSanPham = useSelector((store) => store.categories.listSanPham);
  const totalPages = Math.ceil(listSanPham.length / ITEMS_PER_PAGE);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage >= totalPages - 3) return [1, '...', ...Array.from({ length: 5 }, (_, i) => totalPages - 4 + i)];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  }, [currentPage, totalPages]);

  const paginatedData = listSanPham.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const toggleBrand = (b) => {
    const next = selectedBrand.includes(b) ? [] : [b];
    setSelectedBrand(next);
    dispatch(getDanhMucSanPhamKhacPhucLoi(next[0] || null));
  };

  const handleSearch = () => {
    dispatch(getSanPhamKhacPhucLoi(selectedBrand, selectedCategory, searchInput));
    setCurrentPage(1);
  };

  useEffect(() => {
    dispatch(getListThuongHieuKhacPhucLoi());
  }, []);

  useEffect(() => {
    if (!listThuongHieu?.length) return;

    const matched = brand ? listThuongHieu.find((th) => th.THUONG_HIEU?.toLowerCase() === brand.toLowerCase()) : listThuongHieu[0];

    if (!matched) return;

    setSelectedBrand(matched.THUONG_HIEU);
    dispatch(getDanhMucSanPhamKhacPhucLoi(matched.THUONG_HIEU));
  }, [listThuongHieu, brand]);

  useEffect(() => {
    if (!selectedBrand) return;

    dispatch(getSanPhamKhacPhucLoi(selectedBrand, selectedCategory, model));
  }, [selectedBrand, selectedCategory]);

  const handleChiTiet = (p) => {
    history.push(`/chi-tiet-khac-phuc?&model=${p.MA_DIEU_CHINH}`);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: '#f8f9fa', minHeight: '100vh' }}>
      <style>{`
      .sp-layout { display: flex; gap: 32px; max-width: 1400px; margin: 0 auto; padding: 36px 20px; }
      .sp-sidebar { width: 300px; flex-shrink: 0; background: #fff; border-radius: 12px; border: 1px solid #f0f0f0; padding: 20px; align-self: flex-start; }
      .sp-filter-bar { display: none; background: #fff; border-radius: 12px; border: 1px solid #f0f0f0; padding: 16px; margin-bottom: 16px; }
      .sp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
        .w-md-auto { width: auto !important; }

        @media (max-width: 768px) {
        .w-md-auto { width: 100% !important; }
        }

      @media (max-width: 768px) {
        .sp-layout { flex-direction: column; padding: 16px; gap: 0; }
        .sp-sidebar { display: none; }
        .sp-filter-bar { display: block; }
        .sp-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      }

      @media (max-width: 480px) {
        .sp-grid { grid-template-columns: 1fr; }
      }
    `}</style>

      <div className="sp-layout">
        {/* ── Sidebar (desktop only) ── */}
        <aside className="sp-sidebar">
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Thương hiệu</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {(listThuongHieu || []).map((th) => (
              <label key={th.$id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#444' }}>
                <input type="radio" name="brand" checked={selectedBrand?.includes(th.THUONG_HIEU)} onChange={() => toggleBrand(th.THUONG_HIEU)} style={{ accentColor: '#2563eb', width: 14, height: 14, minWidth: 14, margin: 0 }} />
                {th.THUONG_HIEU}
              </label>
            ))}
          </div>

          <div style={{ width: '100%', height: 1, background: '#e5e7eb', marginBottom: 20 }} />

          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>Dòng sản phẩm</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#444' }}>
              <input type="radio" name="category" checked={selectedCategory === null} onChange={() => setSelectedCategory(null)} style={{ accentColor: '#2563eb', width: 14, height: 14, minWidth: 14, margin: 0 }} />
              Tất cả
            </label>
            {(listDanhMuc || []).map((dm) => (
              <label key={dm.$id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#444' }}>
                <input type="radio" name="category" checked={selectedCategory === dm.ID} onChange={() => setSelectedCategory(dm.ID)} style={{ accentColor: '#2563eb', width: 14, height: 14, minWidth: 14, margin: 0 }} />
                {dm.DONG_SAN_PHAM}
              </label>
            ))}
          </div>
        </aside>

        {/* ── Main ── */}
        <main style={{ flex: 1, minWidth: 0 }}>
          {/* Filter bar (mobile only) */}
          <div className="sp-filter-bar">
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '0 0 8px', textTransform: 'uppercase' }}>Thương hiệu</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(listThuongHieu || []).map((th) => (
                  <label key={th.$id} style={{ cursor: 'pointer' }}>
                    <input type="radio" name="brand-m" checked={selectedBrand?.includes(th.THUONG_HIEU)} onChange={() => toggleBrand(th.THUONG_HIEU)} style={{ display: 'none' }} />
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 12,
                        padding: '4px 12px',
                        borderRadius: 20,
                        border: `1px solid ${selectedBrand?.includes(th.THUONG_HIEU) ? '#2563eb' : '#e5e7eb'}`,
                        background: selectedBrand?.includes(th.THUONG_HIEU) ? '#eff6ff' : '#fff',
                        color: selectedBrand?.includes(th.THUONG_HIEU) ? '#2563eb' : '#6b7280',
                        fontWeight: selectedBrand?.includes(th.THUONG_HIEU) ? 600 : 400,
                      }}
                    >
                      {th.THUONG_HIEU}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '0 0 8px', textTransform: 'uppercase' }}>Dòng sản phẩm</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <label style={{ cursor: 'pointer' }}>
                  <input type="radio" name="category-m" checked={selectedCategory === null} onChange={() => setSelectedCategory(null)} style={{ display: 'none' }} />
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: 12,
                      padding: '4px 12px',
                      borderRadius: 20,
                      border: `1px solid ${selectedCategory === null ? '#2563eb' : '#e5e7eb'}`,
                      background: selectedCategory === null ? '#eff6ff' : '#fff',
                      color: selectedCategory === null ? '#2563eb' : '#6b7280',
                      fontWeight: selectedCategory === null ? 600 : 400,
                    }}
                  >
                    Tất cả
                  </span>
                </label>
                {(listDanhMuc || []).map((dm) => (
                  <label key={dm.$id} style={{ cursor: 'pointer' }}>
                    <input type="radio" name="category-m" checked={selectedCategory === dm.ID} onChange={() => setSelectedCategory(dm.ID)} style={{ display: 'none' }} />
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 12,
                        padding: '4px 12px',
                        borderRadius: 20,
                        border: `1px solid ${selectedCategory === dm.ID ? '#2563eb' : '#e5e7eb'}`,
                        background: selectedCategory === dm.ID ? '#eff6ff' : '#fff',
                        color: selectedCategory === dm.ID ? '#2563eb' : '#6b7280',
                        fontWeight: selectedCategory === dm.ID ? 600 : 400,
                      }}
                    >
                      {dm.DONG_SAN_PHAM}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Header */}
          <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 12, border: '1px solid #f0f0f0', marginBottom: 24 }}>
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
              <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Danh sách sản phẩm</h1>
              <div className="d-flex align-items-center w-100 w-md-auto gap-2">
                <input className="form-control flex-grow-1" placeholder="Tìm model hoặc serial ..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <button className="btn btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={handleSearch}>
                  Tìm kiếm
                </button>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="sp-grid">
            {paginatedData.map((p, i) => (
              <div
                key={`${p.ID}-${i}`}
                style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden', transition: 'box-shadow .2s, transform .2s', display: 'flex', flexDirection: 'column' }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.10)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ position: 'relative' }}>
                  <ProductPlaceholder brand={p.DONG_SAN_PHAM} />
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      background: 'rgba(255,255,255,0.92)',
                      color: '#2563eb',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 6,
                      border: '1px solid #dbeafe',
                      maxWidth: 'calc(100% - 16px)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.DONG_SAN_PHAM}
                  </span>
                </div>
                <div style={{ padding: '12px 12px 10px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 4px', lineHeight: 1.4 }}>{p.MA_DIEU_CHINH}</h3>
                  <p style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.5, margin: '0 0 12px', flex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.TEN_HANG}</p>
                  <button style={{ width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => handleChiTiet(p)}>
                    Xem chi tiết
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 32 }}>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ ...paginationBtn(false), opacity: currentPage === 1 ? 0.3 : 1 }}>
              ‹
            </button>
            {pageNumbers.map((n, i) =>
              n === '...' ? (
                <span key={i} style={{ padding: '0 4px', color: '#aaa' }}>
                  …
                </span>
              ) : (
                <button key={i} onClick={() => setCurrentPage(n)} style={paginationBtn(currentPage === n)}>
                  {n}
                </button>
              ),
            )}
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ ...paginationBtn(false), opacity: currentPage === totalPages ? 0.3 : 1 }}>
              ›
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DanhMucSanPham;
